import React, { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Play, NotebookPen, Printer, ChevronRight, Star, Sparkles, PiggyBank, Map, Gift } from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, NimnyArt } from '../LabUI';
import { computeDayStatus, getWeeksForCourse } from '../data/lessons';
import { formatNims } from '../utils/nims';
import { openIntroVideo } from '../components/IntroVideoGate';

const ChildHome = () => {
  const { t, tx, user, language, childProgress, getNimBalance, refreshBackendStudent } = useLab();
  const params = useParams();
  const location = useLocation();
  // Allow this component to be mounted under both /lab/max (legacy) and
  // /lab/student/:studentId (dynamic). The URL param takes precedence,
  // then the legacy pathname slug — so parent preview never falls back
  // to the wrong child.
  const childId = resolveLabChildId({
    routeStudentId: params.studentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(childId);
  const progress = childProgress(childId);
  const nimBalance = getNimBalance(childId);
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const weeks = getWeeksForCourse(courseTemplate);

  // Parent preview: hydrate this child's progress + nim state from the
  // backend on mount so completed days appear completed and Копилка shows
  // the right balance even though the parent never logged in as the child.
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  useEffect(() => {
    if (!isParent || !childId) return;
    refreshBackendStudent(childId).catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParent, childId]);

  // Empty curriculum (e.g. Anita in Phase 0): show a calm placeholder
  // instead of crashing on `week1.days[0]`. We must NEVER fall back to Max.
  if (!weeks.length) {
    return (
      <LabLayout>
        <section className="text-center mt-6">
          <h1
            className="text-3xl text-[#f3d9a4]"
            style={{ fontFamily: 'var(--font-heading)' }}
            data-testid="child-home-title"
          >
            {t('labTitle')}
          </h1>
        </section>
        <Card className="mt-8 p-6 text-center" data-testid="child-home-empty-course">
          <NimnyArt size="md" />
          <p className="mt-4 text-amber-100/85 font-serif leading-relaxed">
            {language === 'ru'
              ? 'Курс Аниты сейчас готовится.'
              : 'Anita curriculum is being prepared.'}
          </p>
        </Card>
      </LabLayout>
    );
  }

  // Find the most relevant "current" day:
  // First non-completed open / in_progress day in week-1.
  const week1 = weeks.find((w) => w.id === 'week-1');
  const currentDay = (week1?.days || []).find((d) => {
    const s = computeDayStatus(progress, week1.id, d.id, weeks);
    return s === 'open' || s === 'in_progress';
  }) || week1.days[0];

  const currentStatus = computeDayStatus(progress, week1.id, currentDay.id, weeks);
  const lessonPath = `${base}/${week1.id}/${currentDay.id}`;

  // Week 10 Quest Map shortcut: visible the moment Week 10 Day 1 unlocks
  // (i.e. Week 9 Day 5 is completed). Hidden before then.
  const week10Day1Status = computeDayStatus(progress, 'week-10', 'day-1', weeks);
  const questMapAvailable = week10Day1Status !== 'locked';

  return (
    <LabLayout>
      {/* Hero title */}
      <section className="text-center mt-2">
        <h1
          className="text-3xl sm:text-4xl text-[#f3d9a4] leading-[1.1]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="child-home-title"
        >
          {t('labTitle')}
        </h1>
        <p
          className="mt-1 text-amber-200/70"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {t('labName')}
        </p>
        <p className="mt-3 text-amber-100/70 font-serif text-sm leading-relaxed px-2">
          {t('labTagline')}
        </p>
      </section>

      {/* Nimny portrait card */}
      <Card className="mt-7 p-5 flex items-center justify-center">
        <NimnyArt size="lg" />
      </Card>

      {/* Today's mission */}
      <Card className="mt-5 p-5" data-testid="child-home-today-card">
        <div className="text-center">
          <p className="text-amber-200/65 font-sans text-xs uppercase tracking-wider">
            {t('todayLanternOpen')}
          </p>
          <div className="mt-2 flex items-center justify-center gap-3">
            <div>
              <div
                className="text-xl text-[#f3d9a4]"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Week 1 · Day 1
              </div>
              <div className="text-amber-100/85 font-serif text-base mt-0.5">
                — {tx(currentDay.title)}
              </div>
            </div>
            <Star className="w-7 h-7 text-amber-300/85 drop-shadow-[0_0_10px_rgba(212,165,116,0.55)]" />
          </div>
          {/* tiny progress dot row, decorative */}
          <div className="mt-4 flex items-center gap-1.5 justify-center">
            <span className="w-2 h-2 rounded-full bg-amber-300/85 shadow-[0_0_8px_rgba(212,165,116,0.6)]" />
            <span className="flex-1 max-w-[140px] h-px bg-amber-300/15" />
            <span className="w-2 h-2 rounded-full bg-amber-300/25" />
          </div>
        </div>
      </Card>

      {/* Three mode buttons */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <ModeButton
          to={`${lessonPath}#online`}
          icon={Play}
          label={t('startOnline')}
          testid="mode-online"
        />
        <ModeButton
          to={`${lessonPath}#notebook`}
          icon={NotebookPen}
          label={t('workInNotebook')}
          testid="mode-notebook"
        />
        <ModeButton
          to={`${lessonPath}/print`}
          icon={Printer}
          label={t('print')}
          testid="mode-print"
        />
      </div>

      {/* Path link */}
      <Link
        to={`${base}/path`}
        className="mt-4 block"
        data-testid="child-home-path-link"
      >
        <Card className="p-4 flex items-center gap-3 hover:bg-amber-300/5 transition-colors">
          <span className="w-10 h-10 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
            <Sparkles className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/90 font-sans">{t('menuMyPath')}</div>
            <div className="text-amber-200/55 text-xs font-serif">
              {t('pathSubtitle')}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-200/45" />
        </Card>
      </Link>

      {/* Kopilka Нимни card */}
      <Link
        to={`${base}/kopilka`}
        className="mt-3 block"
        data-testid="child-home-kopilka-link"
      >
        <Card className="p-4 flex items-center gap-3 hover:bg-amber-300/5 transition-colors">
          <span className="w-10 h-10 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
            <PiggyBank className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/90 font-sans">
              {t('kopilkaTitle')}
            </div>
            <div
              className="text-amber-200/65 text-xs font-serif"
              data-testid="child-home-kopilka-subtitle"
            >
              {t('kopilkaBalanceLabel')} {formatNims(nimBalance)}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-200/45" />
        </Card>
      </Link>

      {/* Prize Shop shortcut */}
      <Link
        to={`${base}/prize-shop`}
        className="mt-3 block"
        data-testid="child-home-prize-shop-link"
      >
        <Card className="p-4 flex items-center gap-3 hover:bg-amber-300/5 transition-colors">
          <span className="w-10 h-10 rounded-full bg-amber-300/12 border border-amber-300/25 flex items-center justify-center text-amber-200/95">
            <Gift className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/95 font-sans">
              {tx({ ru: 'Лавка Нимни', en: 'Nimny Prize Shop' })}
            </div>
            <div className="text-amber-200/65 text-xs font-serif">
              {tx({
                ru: 'Посмотри, на какие призы хватает нимов.',
                en: "See which prizes your nims can unlock."
              })}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-200/45" />
        </Card>
      </Link>

      {/* Week 10 Quest Map shortcut — only when Week 10 is reachable */}
      {questMapAvailable && (
        <Link
          to={`${base}/week-10/quest`}
          className="mt-3 block"
          data-testid="child-home-quest-map-link"
        >
          <Card className="p-4 flex items-center gap-3 hover:bg-amber-300/5 transition-colors border-amber-300/30 shadow-[0_0_18px_rgba(243,217,164,0.18)]">
            <span className="w-10 h-10 rounded-full bg-amber-300/15 border border-amber-300/30 flex items-center justify-center text-amber-200/95">
              <Map className="w-[18px] h-[18px]" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[#f5ebe0]/95 font-sans">
                {t('questMapTitle')}
              </div>
              <div className="text-amber-200/65 text-xs font-serif">
                {t('questMapSubtitle')}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-200/45" />
          </Card>
        </Link>
      )}

      {/* Parent room shortcut */}
      <Link to="/lab/parent" className="mt-3 block" data-testid="child-home-parent-room">
        <Card className="p-4 flex items-center gap-3 hover:bg-amber-300/5 transition-colors">
          <span className="w-10 h-10 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
            <Star className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/90 font-sans">{t('parentRoomCard')}</div>
            <div className="text-amber-200/55 text-xs font-serif">
              {t('parentRoomDesc')}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-200/45" />
        </Card>
      </Link>

      {/* "Watch welcome again" — manual replay of the intro video. Does not
          reset the introSeen flag. */}
      <button
        type="button"
        onClick={openIntroVideo}
        className="mt-4 w-full text-center text-amber-200/65 hover:text-amber-200/95 text-xs font-sans underline-offset-4 hover:underline transition-colors"
        data-testid="child-home-watch-intro"
      >
        <Play className="inline w-3 h-3 mr-1 -mt-0.5" />
        {language === 'ru' ? 'Посмотреть приветствие' : 'Watch welcome again'}
      </button>

      {/* Footer line */}
      <div className="mt-8 text-center text-xs text-amber-200/40 font-sans">
        <span className="lab-brand-mark">Nimny</span>{' '}
        {t('nimnyLightsTheWay')}
      </div>

      {/* Hidden status sentinel for tests */}
      <span className="sr-only" data-testid="current-day-status">
        {currentStatus}
      </span>
    </LabLayout>
  );
};

const ModeButton = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} data-testid={testid}>
    <div className="aspect-square rounded-2xl border border-amber-300/15 bg-[#0e1530]/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2 hover:bg-amber-300/5 transition-colors p-2">
      <Icon className="w-7 h-7 text-amber-300/90 drop-shadow-[0_0_10px_rgba(212,165,116,0.45)]" />
      <span className="text-[#f5ebe0]/85 font-sans text-xs text-center leading-tight px-1">
        {label}
      </span>
    </div>
  </Link>
);

export default ChildHome;
